(ns minorodata.components.home-dashboard.core
  (:require
   ["lucide-react" :refer [ArrowRight DatabaseIcon LayoutListIcon Warehouse]]
   ["react" :as react]
   [minorodata.app.handlers :refer [dispatch]]
   [minorodata.app.posthog-events :as posthog-events]
   [minorodata.components.connections.drawers :as connections-drawers]
   [minorodata.components.home-dashboard.action-area :as action-area]
   [minorodata.components.home-dashboard.extracts :as extracts]
   [minorodata.components.home-dashboard.hero :as hero]
   [minorodata.components.home-dashboard.sources-breakdown :as sources-breakdown]
   [minorodata.components.home-dashboard.toolbar :as toolbar]
   [minorodata.components.home-dashboard.transforms :as transforms]
   [minorodata.components.warehouse-usage.summary :as warehouse-usage]
   [re-posh.core :as re-posh]
   [reitit.frontend.easy :as rt.easy]
   [ui.button :as button]
   [ui.page :as page]
   [ui.tabs :as tabs]))


(defn- warehouse-usage-tab-enabled?
  []
  (and @(re-posh/subscribe [:unleash/is-enabled "cost-section"])
       @(re-posh/subscribe [:app.subs/enabled-for-bigquery :costs])
       @(re-posh/subscribe [:users.subs/has-permissions? #{:warehouse-health/read}])))

(defn page-content []
  (let [ai-enabled?    @(re-posh/subscribe [:unleash/is-enabled "fe.ai-chat"])
        query          @(re-posh/subscribe [:app-route/prop :app-route/query])
        usage-enabled? (warehouse-usage-tab-enabled?)
        valid-tabs     (cond-> #{"extracts" "transforms"}
                         usage-enabled? (conj "warehouse-usage"))
        default-tab    "extracts"
        effective-tab  (if (contains? valid-tabs (:tab query))
                         (:tab query)
                         default-tab)]
    [:div
     {:class "flex min-h-full w-full flex-col bg-kleene-background-secondary"}
     (when ai-enabled?
       [hero/overview-hero])

     [:div
      {:class "container mx-auto flex flex-col px-6 py-8"}
      [action-area/overview-action-area]

      [tabs/tabs-root {:value           effective-tab
                       :class           "mt-12"
                       :on-value-change (fn [tab]
                                          (dispatch [:posthog/capture {:event (:overview/tab-selected posthog-events/events)
                                                                       :props {:tab tab}}])
                                          (dispatch [:app.events/navigate-query {:tab tab}]))}
       [:div {:class "flex items-stretch justify-between gap-4 border-b border-kleene-layout-border"}
        [tabs/tabs-list {:variant :underlined
                         :class   "border-b-0 self-end"}
         [tabs/tabs-trigger {:value   "extracts"
                             :variant :underlined}
          [:span {:class "inline-flex items-center gap-2"}
           [:> DatabaseIcon {:class "h-4 w-4"}]
           "Extracts"]]

         [tabs/tabs-trigger {:value   "transforms"
                             :variant :underlined}
          [:span {:class "inline-flex items-center gap-2"}
           [:> LayoutListIcon {:class "h-4 w-4"}]
           "Transforms"]]

         (when usage-enabled?
           [tabs/tabs-trigger {:value   "warehouse-usage"
                               :variant :underlined}
            [:span {:class "inline-flex items-center gap-2"}
             [:> Warehouse {:class "h-4 w-4"}]
             "Warehouse usage"]])]

        [:div {:class "flex items-center gap-2 pb-2 shrink-0"}
         (if (= effective-tab "warehouse-usage")
           [:<>
            [toolbar/refresh-button {:on-refresh warehouse-usage/refresh!}]
            [warehouse-usage/date-selector]
            [button/button {:href       (rt.easy/href :warehouse-usage)
                            :variant    :secondary
                            :icon-right ArrowRight
                            :on-click   #(action-area/track-action "view_full_usage")}
             "View full usage"]]
           [:<>
            [toolbar/refresh-button]
            [toolbar/time-select-dropdown]])]]

       [tabs/tabs-content {:value "extracts"}
        [:div {:class "grid grid-cols-1 gap-6 pt-4 md:grid-cols-2 xl:grid-cols-4"}
         [extracts/extracts-success-rate-card]
         [extracts/extracts-started-card]
         [extracts/extracts-succeeded-card]
         [extracts/extracts-failed-card]
         [extracts/execution-time-card]
         [:div {:class "md:col-span-2 xl:col-span-4"}
          [sources-breakdown/sources-breakdown]]
         [:div {:class "md:col-span-2 xl:col-span-4"}
          [extracts/extracts-executions-chart]]
         [:div {:class "xl:col-span-2"}
          [extracts/slowest-extracts-table]]
         [:div {:class "xl:col-span-2"}
          [extracts/recently-failed-extracts-table]]
         [:div {:class "md:col-span-2 xl:col-span-4"}
          [extracts/total-connector-runtime-chart]]]]

       [tabs/tabs-content {:value "transforms"}
        [:div {:class "grid grid-cols-1 gap-6 pt-4 md:grid-cols-2 xl:grid-cols-4"}
         [transforms/transforms-finished-card]
         [transforms/transforms-in-queue-card]
         [transforms/transforms-running-card]
         [:div {:class "md:col-span-2 xl:col-span-4"}
          [transforms/transforms-finished-chart]]
         [:div {:class "xl:col-span-2"}
          [transforms/transforms-slowest-table]]
         [:div {:class "xl:col-span-2"}
          [transforms/recently-failed-transforms-table]]
         [:div {:class "md:col-span-2 xl:col-span-4"}
          [transforms/transforms-runtime-distribution-chart]]]]

       (when usage-enabled?
         [tabs/tabs-content {:value "warehouse-usage"}
          [warehouse-usage/usage-tab]])]]]))

(defn page' []
  (let [query           @(re-posh/subscribe [:app-route/prop :app-route/query])
        usage-enabled?  (warehouse-usage-tab-enabled?)
        valid-tabs      (cond-> #{"extracts" "transforms"}
                          usage-enabled? (conj "warehouse-usage"))
        default-tab     "extracts"
        gating-pending? @(re-posh/subscribe [:mold-http/loading? :connections.api/fetch-warehouse-status])]
    (react/useEffect
     (fn []
       (dispatch [:analytics.events/init])

       ;; Required for the Overview "Create transform" dialog.
       (dispatch [:pipeline-editor.api/fetch-transform-groups])
       js/undefined)
     #js [])

    (react/useEffect
     (fn []
       (when (and (not gating-pending?) (not (contains? valid-tabs (:tab query))))
         (dispatch [:app.events/navigate-query {:tab default-tab}]))
       js/undefined)
     #js [gating-pending? usage-enabled? (:tab query)]))

  [page/page-root
   [page/page-content {:class "p-0"}
    [page-content]]

   [:f> connections-drawers/drawers]])

(defn page []
  [:f> page'])

(defn old-page []
  (dispatch [:app.events/navigate :overview])
  (fn [] [:div]))
